import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Button from '../../Components/Button/Button';
import { postApi } from '../../Services/apiService';
import { toast } from 'react-toastify';
import {
  getPostLoginRoute,
  getRoleFromResponse,
  isTenantRole,
  persistSession,
  clearPendingLogin,
  PENDING_LOGIN_KEYS,
} from '../../Utils/postLoginNavigation';

const OTP_LENGTH = 6;
const EMPTY_OTP = Array(OTP_LENGTH).fill('');
const RESEND_COOLDOWN_SECONDS = 30;

const LoginOTPVerification = () => {
  const [otp, setOtp] = useState(EMPTY_OTP);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_SECONDS);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef([]);

  const redirectTo = location.state?.redirect;
  const asRole = location.state?.as;

  useEffect(() => {
    // The email comes from the login screen; localStorage is the fallback for a
    // page refresh in the middle of the challenge.
    const emailFromState = location.state?.email;
    const emailFromStorage = localStorage.getItem(PENDING_LOGIN_KEYS.email);
    const resolvedEmail = emailFromState || emailFromStorage;

    if (!resolvedEmail) {
      // Nothing to verify against — send the user back to sign in.
      navigate('/login', { replace: true });
      return;
    }

    setEmail(resolvedEmail);
    localStorage.setItem(PENDING_LOGIN_KEYS.email, resolvedEmail);

    // Add animation classes on mount
    const infoSection = document.querySelector('.login-info');
    const formSection = document.querySelector('.login-form-section');
    const formCard = document.querySelector('.login-form-card');

    if (infoSection) infoSection.classList.add('animate-fade-in-left');
    if (formSection) formSection.classList.add('animate-fade-in-right');
    if (formCard) formCard.classList.add('animate-scale-in');

    // Focus first input on mount
    inputRefs.current?.[0]?.focus?.();
  }, [location, navigate]);

  // Countdown that gates the "Resend Code" link.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timerId = setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timerId);
  }, [resendIn]);

  const resetOtp = () => {
    setOtp(EMPTY_OTP);
    inputRefs.current?.[0]?.focus?.();
  };

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (errors.otp) {
      setErrors((prev) => ({ ...prev, otp: '' }));
    }

    // Auto-focus next input
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current?.[index + 1]?.focus?.();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current?.[index - 1]?.focus?.();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    if (new RegExp(`^\\d{${OTP_LENGTH}}$`).test(pastedData)) {
      setOtp(pastedData.split(''));
      inputRefs.current?.[OTP_LENGTH - 1]?.focus?.();

      if (errors.otp) {
        setErrors((prev) => ({ ...prev, otp: '' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const otpString = otp.join('');

    if (otpString.length !== OTP_LENGTH || !/^\d+$/.test(otpString)) {
      setErrors({ otp: `Please enter the complete ${OTP_LENGTH}-digit code` });
      inputRefs.current?.[0]?.focus?.();
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await postApi('/auth/verify-otp', {
        email,
        otp: otpString,
      });

      if (response?.statusCode === 200) {
        // Fall back to the login response for anything the OTP response omits.
        let pendingLogin = null;
        try {
          const stored = localStorage.getItem(PENDING_LOGIN_KEYS.response);
          pendingLogin = stored ? JSON.parse(stored) : null;
        } catch (parseError) {
          console.error('Error reading pending login data:', parseError);
        }

        const role =
          getRoleFromResponse(response) ??
          getRoleFromResponse(pendingLogin) ??
          localStorage.getItem(PENDING_LOGIN_KEYS.role);

        // Someone who came in through "Start Tenant Screening" must be a tenant.
        if (redirectTo && asRole === 'tenant' && !isTenantRole(role)) {
          setErrors({
            otp: 'You are signed in as a landlord. Please sign out and sign up as a tenant to apply for this property.',
          });
          toast.error('You are signed in as a landlord. Sign out and sign up as a tenant to apply.');
          setIsLoading(false);
          return;
        }

        toast.success(response?.message || 'Logged in successfully');

        persistSession(response);
        clearPendingLogin();

        const destination = getPostLoginRoute(response, redirectTo);

        // The signup verification screen reads these to know who it is verifying.
        if (destination === '/verify-otp') {
          localStorage.setItem('signupEmail', email);
          if (role !== undefined && role !== null && role !== '') {
            localStorage.setItem('signupRole', String(role));
          }
          navigate('/verify-otp', { replace: true, state: redirectTo ? { redirect: redirectTo } : undefined });
          return;
        }

        navigate(destination, { replace: true });
      } else {
        setErrors({ otp: response?.message || 'Invalid verification code' });
        resetOtp();
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Verification failed. Please try again.';
      setErrors({ otp: errorMessage });
      resetOtp();
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      toast.error('Session expired. Please sign in again.');
      navigate('/login', { replace: true });
      return;
    }

    setIsResending(true);
    try {
      const response = await postApi('/auth/resend-otp', { email });

      if (response?.statusCode === 200) {
        toast.success(response?.message || 'Verification code sent successfully');
        setErrors({});
        resetOtp();
        setResendIn(RESEND_COOLDOWN_SECONDS);
      } else {
        toast.error(response?.message || 'Failed to resend code');
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || 'Failed to resend code. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-info">
          <div className="login-logo">
            <Link to="/" onClick={() => localStorage.clear()}>
              <img src={window.getPublicUrl("/assets/images/Logo.png")} alt="ClearRent" className="login-logo-img" />
            </Link>
          </div>
          <h1 className="login-welcome">Welcome back</h1>
          <p className="login-description">
            Access your dashboard to manage properties and review tenant screenings.
          </p>
        </div>
        <div className="login-form-section">
          <h2 className="login-form-title">Verify It's You</h2>
          <p className="login-form-subtitle">
            Please enter the 6 digit code sent to {email || 'your email'}
          </p>
          <form onSubmit={handleSubmit} className={`login-form-card ${Object.keys(errors).length > 0 ? 'has-errors' : ''}`} noValidate>
            <div className="login-form-header">Security Verification</div>
            <div className={`login-form-fields ${Object.keys(errors).length > 0 ? 'has-errors' : ''}`}>
              <div className="otp-input-container">
                <div className="otp-inputs">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { inputRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      disabled={isLoading}
                      className={`otp-input ${errors.otp ? 'otp-input-error' : ''}`}
                      required
                    />
                  ))}
                </div>
                {errors.otp && (
                  <div className="error-message-container has-error">
                    <span className="error-message">{errors.otp}</span>
                  </div>
                )}
              </div>
            </div>
            <Button
              type="submit"
              variant="success"
              className="login-submit-button"
              disabled={isLoading}
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </Button>
            <p className="login-signup-link">
              Didn't receive the code?{' '}
              <button
                type="button"
                onClick={handleResendCode}
                className="resend-code-link"
                disabled={isLoading || isResending || resendIn > 0}
              >
                {resendIn > 0 ? `Resend Code in ${resendIn}s` : (isResending ? 'Sending...' : 'Resend Code')}
              </button>
            </p>
            <p className="login-signup-link">
              <Link to="/login" onClick={clearPendingLogin}>Back to sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginOTPVerification;
